import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await apiService.auth.login(credentials);
      // Backend wraps response: { success: true, data: { user, accessToken, refreshToken }, message, timestamp }
      const { accessToken, refreshToken, user } = response.data.data;
      
      // Store tokens
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      
      return { user, accessToken, refreshToken };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await apiService.auth.register(userData);
      // Backend wraps response: { success: true, data: { user, accessToken, refreshToken, businessAccountId }, message, timestamp }
      const { user, accessToken, refreshToken, businessAccountId } = response.data.data;
      
      // Store tokens
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      if (businessAccountId) {
        localStorage.setItem('businessAccountId', businessAccountId);
      }
      
      return { user, accessToken, refreshToken, businessAccountId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await apiService.auth.logout();
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      return null;
    } catch (error) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.auth.getProfile();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateOnboardingStep = createAsyncThunk(
  'auth/updateOnboardingStep',
  async (stepPayload, { rejectWithValue }) => {
    try {
      // Handle both old format (just step number) and new format ({ step, data })
      const payload = typeof stepPayload === 'number' 
        ? { step: stepPayload } 
        : stepPayload;
      
      const response = await apiService.auth.updateOnboardingStep(payload);
      // Backend wraps response: { success: true, data: user, message }
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const completeOnboarding = createAsyncThunk(
  'auth/completeOnboarding',
  async (formData = {}, { rejectWithValue }) => {
    try {
      const response = await apiService.auth.completeOnboarding(formData);
      // Backend wraps response: { success: true, data: user, message }
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const saveOnboardingStep = createAsyncThunk(
  'auth/saveOnboardingStep',
  async ({ step, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.auth.saveOnboardingStep({ step, data });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const completeOnboardingWithData = createAsyncThunk(
  'auth/completeOnboardingWithData',
  async (onboardingData, { rejectWithValue }) => {
    try {
      const response = await apiService.auth.completeOnboardingWithData(onboardingData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const checkBusinessRegistration = createAsyncThunk(
  'auth/checkBusinessRegistration',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.auth.checkBusinessRegistration();
      // Backend wraps response: { success: true, data: { businessRegistered, businessRegistrationDate }, message }
      return response.data.data || { businessRegistered: false };
    } catch (error) {
      // Default to not registered if check fails
      console.error('Business registration check error:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  user: null,
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
  businessAccountId: localStorage.getItem('businessAccountId'),
  isAuthenticated: !!localStorage.getItem('accessToken'),
  businessRegistered: localStorage.getItem('businessRegistered') === 'true',
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCredentials: (state, action) => {
      const { user, accessToken, refreshToken } = action.payload;
      state.user = user;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.isAuthenticated = true;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
    },
    clearCredentials: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.businessAccountId = null;
      state.isAuthenticated = false;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('businessAccountId');
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.businessAccountId = action.payload.businessAccountId;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
      })
      // Get Profile
      .addCase(getProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Onboarding Step
      .addCase(updateOnboardingStep.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOnboardingStep.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(updateOnboardingStep.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Complete Onboarding
      .addCase(completeOnboarding.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeOnboarding.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(completeOnboarding.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Check Business Registration
      .addCase(checkBusinessRegistration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkBusinessRegistration.fulfilled, (state, action) => {
        state.loading = false;
        state.businessRegistered = action.payload.businessRegistered;
        localStorage.setItem('businessRegistered', action.payload.businessRegistered);
        state.error = null;
      })
      .addCase(checkBusinessRegistration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCredentials, clearCredentials } = authSlice.actions;
export default authSlice.reducer;

